import { useCallback, useMemo } from "react";
import { addDays, isAfter } from "date-fns";

import { Modal } from "@covertree/ui";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";

import { PaymentMethod, RenewalStatus } from "@covertree/agent-portal-graphql";
import { useGlobalLoading } from "@hooks/common";

import { useAddNewStripePaymentMethod } from "./useAddNewStripePaymentMethod";
import { PaymentMethodsList } from "./PaymentMethodsList";
import { NewPaymentMethodForm } from "./NewPaymentMethodForm";
import { usePolicyDetails } from "../../../../../PolicyDetailsContext";

const stripePromise = loadStripe(`${process.env.VITE_STRIPE_PK_KEY}`!);

interface Props {
  open: boolean;
  onClose(): void;
}

export function PaymentMethodsModal({ open, onClose }: Props) {
  const { policy } = usePolicyDetails();

  const latestRenewal = useMemo(() => {
    if (!policy?.renewals?.length) return null;

    return policy?.renewals?.reduce((latest, current) => {
      if (!latest) return current;
      return new Date(current.renewalTerm.effectiveDate) >
        new Date(latest.renewalTerm.effectiveDate)
        ? current
        : latest;
    });
  }, [policy]);

  const shouldTriggerRenewalReissuance = useMemo(() => {
    if (!latestRenewal) return false;

    const currentDate = new Date();
    const policyEndDatePlus7Days = addDays(new Date(policy.endDate), 7);

    return (
      latestRenewal.status === RenewalStatus.PaymentFailed &&
      (latestRenewal.paymentMethod === PaymentMethod.CreditDebitCard ||
        latestRenewal.paymentMethod === PaymentMethod.BankAccount) &&
      !isAfter(currentDate, policyEndDatePlus7Days)
    );
  }, [policy, latestRenewal]);

  const {
    isAddNewPaymentMethodModeOpen,
    setAddNewPaymentMethodModeOpen,
    onAddNewPaymentMethod,
    loading: isAddingNewPaymentMethodLoading,
  } = useAddNewStripePaymentMethod(
    policy.policyLocator,
    shouldTriggerRenewalReissuance
  );

  const { loading } = useGlobalLoading();

  const onModalClose = useCallback(() => {
    onClose();
    setAddNewPaymentMethodModeOpen(false);
  }, [onClose]);

  const stripeOptions = useMemo<StripeElementsOptions>(
    () => ({
      appearance: {
        theme: "flat",
        labels: "above",
        variables: {
          colorPrimary: "#006B5B",
          colorWarning: "#F47A35",
          colorDanger: "#DA2C2C",
          colorText: "#000000",
          fontFamily:
            'PolySans, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
        },
      },
      loader: "always",
      locale: "en",
      mode: "setup",
      paymentMethodOptions: {
        us_bank_account: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          verification_method: "skip",
        },
      },
      setupFutureUsage: "off_session",
      currency: "usd",
    }),
    []
  );

  return (
    <>
      <Modal
        maxWidth="md"
        title="Update payment method"
        open={open}
        onClose={onModalClose}
        disableClose={isAddingNewPaymentMethodLoading || loading}>
        {!isAddNewPaymentMethodModeOpen && (
          <PaymentMethodsList
            cards={policy.paymentMethods.creditCardPaymentMethods}
            achMethods={policy.paymentMethods.achPaymentMethods}
            policyLocator={policy.policyLocator}
            onClose={onClose}
            onAddNewPaymentMethod={() => setAddNewPaymentMethodModeOpen(true)}
            shouldTriggerRenewalReissuance={shouldTriggerRenewalReissuance}
            renewalLocator={latestRenewal?.renewalLocator}
          />
        )}

        {isAddNewPaymentMethodModeOpen && (
          <Elements stripe={stripePromise} options={stripeOptions}>
            <NewPaymentMethodForm
              onCancel={() => setAddNewPaymentMethodModeOpen(false)}
              onSubmit={onAddNewPaymentMethod}
              isLoading={isAddingNewPaymentMethodLoading}
              renewalLocator={latestRenewal?.renewalLocator}
              shouldTriggerRenewalReissuance={shouldTriggerRenewalReissuance}
            />
          </Elements>
        )}
      </Modal>
    </>
  );
}
